/**
 * Choosing where the selected notes are taken on the instrument.
 *
 * A note arrives on a tab staff with the lowest position that can play it, which is the
 * guess a player most often keeps. What they change is the hand: the same phrase played up
 * the neck on one string instead of across four, a run kept in fifth position, an open
 * string used because it can ring on. That is what this layer is for.
 *
 * The two doors move in opposite directions and both are needed. {@link setString} keeps
 * the note and moves the hand -- the pitch is unchanged and the fret follows the string.
 * {@link setFret} keeps the hand and changes the note -- which is what typing a number into
 * a tab staff does, and why a fret is not a display of the pitch but a statement about it.
 *
 * Nothing here invents a tuning. A part that is not fretted refuses in words rather than
 * quietly growing strings.
 */

import type { Command } from "../commands/types";
import type { PartId } from "../document/ids";
import { spell } from "../document/pitch";
import type { NotePitch } from "../document/pitch";
import { resolveAttributes } from "../document/query";
import { HIGHEST_FRET, fretFor, openMidi, positionFor } from "../document/tab";
import type { StringTuning, TabPosition } from "../document/tab";
import type { Score } from "../document/types";
import { selectedNotes } from "./selection";
import type { Selection } from "./selection";

/** The strings a part is played on, or `null` when it is written in pitches. */
export function tuningOf(score: Score, partId: PartId | null): readonly StringTuning[] | null {
  if (partId === null) return null;
  return score.parts.find((part) => part.id === partId)?.instrument.tuning ?? null;
}

/** The tuning the selection is written on, or `null` when it spans none or several parts. */
export function tuningInSelection(
  score: Score,
  selection: Selection,
): readonly StringTuning[] | null {
  const parts = new Set(selectedNotes(score, selection).map((at) => at.partId));
  if (parts.size !== 1) return null;
  return tuningOf(score, [...parts][0] ?? null);
}

/** The positions the selected notes are taken at, which is what a panel shows. */
export function positionsInSelection(score: Score, selection: Selection): readonly TabPosition[] {
  const places: TabPosition[] = [];
  for (const at of selectedNotes(score, selection)) {
    for (const pitch of at.event.pitches) if (pitch.tab !== null) places.push(pitch.tab);
  }
  return places;
}

/** How a string is spoken of in a refusal: by its number and what it is tuned to. */
function stringName(tuning: readonly StringTuning[], string: number): string {
  const open = tuning[string - 1];
  return open === undefined ? `string ${string}` : `string ${string} (${open.step}${open.octave})`;
}

/**
 * The head the user is on: the one an edit here acts on.
 *
 * A chord on a fretted instrument is spread across strings, so "put this on the third
 * string" and "make this the fifth fret" can only mean one of its notes -- the one the
 * caret is on, which is what `pitchIndex` already records. A single note is head 0, so the
 * ordinary case needs no thought. Where a selection spans several chords the same position
 * in each is taken, clamped to what is there.
 */
function headAt(pitches: readonly NotePitch[], selection: Selection): number {
  return Math.min(Math.max(selection.pitchIndex, 0), pitches.length - 1);
}

/** A chord's other strings, which nothing may be moved onto. */
function otherStrings(pitches: readonly NotePitch[], index: number): ReadonlySet<number> {
  const taken = new Set<number>();
  pitches.forEach((pitch, at) => {
    if (at !== index && pitch.tab !== null) taken.add(pitch.tab.string);
  });
  return taken;
}

/**
 * Play the selected note on that string, keeping the note itself.
 *
 * A string that cannot reach one of them refuses the whole edit rather than moving the rest:
 * a passage half moved is a passage the player has to check note by note, which is worse
 * than one that did not move. A string another note of the same chord is already on is
 * refused for the same reason it would be unplayable.
 */
export function setString(score: Score, selection: Selection, string: number): Command | string {
  const tuning = tuningInSelection(score, selection);
  if (tuning === null) return "select notes on one fretted staff first";
  if (string < 1 || string > tuning.length) return "this instrument has no such string";

  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const at of found) {
    const index = headAt(at.event.pitches, selection);
    const pitch = at.event.pitches[index];
    if (pitch === undefined) continue;
    if (otherStrings(at.event.pitches, index).has(string)) {
      return `another note of that chord is on ${stringName(tuning, string)}`;
    }
    const fret = fretFor(tuning, string, pitch);
    if (fret === null) return `${stringName(tuning, string)} cannot play every selected note`;
    const pitches = at.event.pitches.map((was, on) =>
      on === index ? { ...was, tab: { string, fret } } : was,
    );
    commands.push({ kind: "setPitches", eventId: at.event.id, pitches });
  }
  return { kind: "batch", label: `string ${string}`, commands };
}

/**
 * Stop the selected note at that fret, which changes what it sounds.
 *
 * The note keeps the string it was on, because that is the hand the player has already
 * chosen and only the stopping finger is being moved. A note with no position yet is put on
 * the string its pitch already lies nearest, so that typing a fret into a bar of ordinary
 * notation does something sensible instead of refusing.
 */
export function setFret(score: Score, selection: Selection, fret: number): Command | string {
  const tuning = tuningInSelection(score, selection);
  if (tuning === null) return "select notes on one fretted staff first";
  if (fret < 0 || fret > HIGHEST_FRET) return `a fret is a number from 0 to ${HIGHEST_FRET}`;

  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const at of found) {
    const { key } = resolveAttributes(score, at.measureIndex);
    const index = headAt(at.event.pitches, selection);
    const pitch = at.event.pitches[index];
    if (pitch === undefined) continue;
    const string =
      pitch.tab?.string ??
      positionFor(tuning, pitch, otherStrings(at.event.pitches, index))?.string ??
      1;
    const open = openMidi(tuning, string);
    if (open === null) return "this instrument has no such string";
    const sounded = spell(open + fret, key.fifths);
    const pitches = at.event.pitches.map((was, on) =>
      on === index ? { ...was, ...sounded, tab: { string, fret } } : was,
    );
    commands.push({ kind: "setPitches", eventId: at.event.id, pitches });
  }
  return { kind: "batch", label: `fret ${fret}`, commands };
}

/**
 * Put every selected note back at its lowest position, the guess a fresh note is given.
 *
 * This is what a player asks for after pasting a passage from elsewhere, where the frets
 * came from another hand or from nowhere at all.
 */
export function refret(score: Score, selection: Selection): Command | string {
  const tuning = tuningInSelection(score, selection);
  if (tuning === null) return "select notes on one fretted staff first";

  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const at of found) {
    const pitches: NotePitch[] = [];
    // A chord is placed head by head, each one keeping off the strings the ones before it
    // took, because the hand has one finger per string.
    const taken = new Set<number>();
    for (const pitch of at.event.pitches) {
      const place = positionFor(tuning, pitch, taken);
      if (place === null) return "this instrument cannot reach every selected note";
      taken.add(place.string);
      pitches.push({ ...pitch, tab: place });
    }
    commands.push({ kind: "setPitches", eventId: at.event.id, pitches });
  }
  return { kind: "batch", label: "refret", commands };
}
